import React, { useEffect, useState, Suspense } from 'react';
import { Button, Collapse, Tabs, message, Tag, Drawer } from 'antd';
import { Row } from 'reactstrap';
import TinBday from './TinBday';
import axios from '../../helpers/Axios';
import { useAuth } from '../../context/AuthProvider';
import { Colxx } from '../../components/common/CustomBootstrap';
import { img_checklist, file_nspo, file_mc19s2019, file_gis_stock_2026, file_gis_non_stock_2026 } from '../../assets/files';

const DynamicDoughnutChart = React.lazy(() => import('./DashboardChart'));

// ── Design tokens matching Sign In Page ──────────────────────────────────────
const BRAND_GREEN = '#0e8366';

const CARD = {
  background: '#ffffff',
  borderRadius: 20,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
  border: '1px solid #f3f4f6',
};

// ── Content data ─────────────────────────────────────────────────────────────
const announcements = [
  {
    title: 'Non-Stock and Non-Profit Organizations (NSPO) including Foundations',
    content: (
      <blockquote style={{ borderLeft: `3px solid ${BRAND_GREEN}`, paddingLeft: 16, margin: 0 }}>
        <p style={{ marginBottom: 8 }}>
          The forms as provided in <strong>Annex 68-C of the Revised SRC Rule 68</strong> must be accomplished &amp; submitted by non-stock and non-profit organizations including foundations. NSPO Form-1, NSPO Form-2 and NSPO Form-3 must be submitted by all registered non-stock &amp; non-profit organizations. For those with annual contributions or donations of <strong>₱ 500,000 or more</strong>, NSPO Form-4, NSPO Form-5 and NSPO Form-6 must also be submitted with the AFS. These schedules need not be covered by an Auditor's Report.
        </p>
        <footer style={{ fontSize: 12, color: '#9ca3af' }}>
          Source: <cite>SEC Revised SRC Rule 68</cite>
        </footer>
      </blockquote>
    ),
  },
  {
    title: 'Receiving Of Reports Filed Through eFAST',
    content: (
      <div style={{ margin: 0 }}>
        <p style={{ marginBottom: 8 }}>Please be informed of the following:</p>
        <ol style={{ paddingLeft: 18, margin: 0 }}>
          <li style={{ marginBottom: 8 }}>
            Reports filed through the <strong>Electronic Filing and Submission Tool (eFAST)</strong> are reviewed chronologically by the Receiving Officers.
          </li>
          <li style={{ marginBottom: 8 }}>
            Review of reports shall be from Mondays to Fridays, 8:00 a.m.–5:00 p.m. Reports filed on Saturdays, Sundays, Holidays and declared work suspensions shall be reviewed on the next working day.
          </li>
          <li style={{ marginBottom: 8 }}>
            <p style={{ margin: 0 }}><strong>Receipt date of report.</strong> A generated reply shall be sent to the filer as temporary receipt of the report filed.</p>
            <p style={{ margin: '4px 0' }}>For accepted reports, the receipt date on the QR code page shall be the date the report was filed to eFAST, except those filed on Saturdays, Sundays, Holidays and declared work suspensions for which the receipt date shall be the next working day.</p>
            <p style={{ margin: '4px 0' }}>For reverted reports, the re-submission of the corrected copy shall be the basis for acceptance and issuance of the QR code page.</p>
            <p style={{ margin: '4px 0' }}>Notification shall be sent to the official email of the company or authorized filer once the report is accepted or reverted. The receipt of the report may be monitored in the eFAST account.</p>
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>The timeframe of receipt of reports may vary depending on the volume of reports filed for the day. For the peak period of filing of reports (April to July),</strong> the QR code page may be sent to the filer within 15 working days. <strong>For the lean period of filing of reports,</strong> the QR code page may be sent from 3 to 5 working days.
          </li>
        </ol>
        <p style={{ marginTop: 8, marginBottom: 4 }}>
          For inquiries, email us at <a href="mailto:ermdinquiry@sec.gov.ph" style={{ color: BRAND_GREEN, fontWeight: 600 }}><u>ermdinquiry@sec.gov.ph</u></a>
        </p>
        <p style={{ margin: 0 }}>Please be guided accordingly.</p>
      </div>
    ),
  },
  {
    title: 'Submission of Reports Through eFAST',
    content: (
      <div style={{ margin: 0 }}>
        <p style={{ marginBottom: 8 }}>
          TO: All Registered Issuers, Financing Companies, Lending Companies, Corporate Governance Institutional Training Providers, And Publicly-Listed Companies Under The Supervision Of The Corporate Governance And Finance Department (CGFD)
        </p>
        <p style={{ marginBottom: 8 }}>
          In line with <strong>SEC Memorandum Circular No. 3 series of 2021</strong>, filers are hereby advised pursuant to the submission of <strong>eleven (11) reports through the Electronic Filing and Submission Tool (eFAST) as required by CGFD.</strong> For more details, please go to:{' '}
          <a
            href="https://www.sec.gov.ph/notices/submission-of-reports-through-efast/#gsc.tab=0"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: BRAND_GREEN, fontWeight: 600 }}
          >
            <u>NOTICE</u>
          </a>
        </p>
        <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13 }}>
          <li>Sworn Certification (MC 18)</li>
          <li>Affidavit of Compliance - Existing Online Lending Platforms (MC 19)</li>
          <li>Affidavit of Compliance - Prospective Online Lending Platforms (MC 19)</li>
          <li>Certificate-SEC Form MCG- 2009</li>
          <li>Completion Report</li>
          <li>Certification of Attendance in Corporate Governance</li>
          <li>ACGR</li>
          <li>Secretary's Certificate Meeting of Board Directors (Appointment)</li>
          <li>Certificate-SEC Form MCG- 2002, 2020 ETC.</li>
          <li>I-ACGR</li>
          <li>MRPT</li>
        </ul>
      </div>
    ),
  },
];

const notices = [
  {
    title: 'Submission of Secondary Reports through eFAST',
    content: (
      <p style={{ margin: 0 }}>
        For details of the released notice, please click{' '}
        <a
          href="https://www.sec.gov.ph/notices/submission-of-secondary-reports-through-efast/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: BRAND_GREEN, fontWeight: 600 }}
        >
          <em>here →</em>
        </a>
      </p>
    ),
  },
  {
    title: 'Submission of Manual on Corporate Governance (MCG) and Mutual Fund Sales Report (ICASR) Through eFAST',
    content: (
      <p style={{ margin: 0 }}>
        For details of the released notice, please click{' '}
        <a
          href="https://www.sec.gov.ph/notices/submission-of-manual-on-corporate-governance-mcg-and-mutual-fund-sales-report-icasr-through-efast"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: BRAND_GREEN, fontWeight: 600 }}
        >
          <em>here →</em>
        </a>
      </p>
    ),
  },
  {
    title: 'Forfeiture of Amnesty Fees Enhanced Compliance Incentives Program',
    content: (
      <p style={{ margin: 0 }}>
        For details of the released notice, please click{' '}
        <a
          href="https://www.sec.gov.ph/notices/forfeiture-of-amnesty-fees-enhanced-compliance-incentives-program"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: BRAND_GREEN, fontWeight: 600 }}
        >
          <em>here →</em>
        </a>
      </p>
    ),
  },
  {
    title: 'Notice On The Auto Acceptance Of AFS In The eFAST',
    content: (
      <div style={{ margin: 0 }}>
        <p style={{ marginBottom: 6 }}>
          Please be informed that financial statements submitted through the Electronic Filing and Submission Tool (eFAST) shall automatically be received and issued a QR Code.
        </p>
        <p style={{ marginBottom: 6 }}>
          Submitted financial statements shall be subject to evaluation by the appropriate department(s) and corresponding penalty (-ies), if warranted, pursuant to existing issuances, and rules and regulations implemented by the Commission.
        </p>
        <p style={{ marginBottom: 6 }}>Please be guided accordingly.</p>
        <p style={{ marginTop: 8, marginBottom: 0, color: '#9ca3af', fontSize: 12 }}>
          Issued on 9 July 2024.
        </p>
      </div>
    ),
  },
];

const templates = [
  {
    title: 'Request for Lifting of Suspension and/or Revocation Templates',
    content: (
      <div>
        <p style={{ marginBottom: 8 }}>
          For copies and verifications of current reportorial form templates, please proceed to the links below:
        </p>
        <ol style={{ paddingLeft: 18, marginBottom: 12 }}>
          <li>
            <b>
              <em>
                <a
                  href="https://www.sec.gov.ph/wp-content/uploads/2024/03/2024Forms_SAMPLE-FORMAT.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: BRAND_GREEN }}
                >
                  Request for Lifting of Suspension and/or Revocation
                </a>
              </em>
            </b>
          </li>
        </ol>
        
        <p style={{ marginTop: 8, marginBottom: 12 }}>
          The Petition for Lifting of Suspension And/Or Revocation shall be filed via email to your corresponding office:
        </p>

        {/* Side-by-side paired layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '8px 12px',
            alignItems: 'baseline',
            fontSize: 13,
          }}
        >
          {[
            { office: '1. The SEC Headquarters', email: 'cmd_petitiontolift_suspension-revocation@sec.gov.ph' },
            { office: '2. Baguio', email: 'amendments_bageo@sec.gov.ph' },
            { office: '3. Tarlac', email: 'secteo@sec.gov.ph' },
            { office: '4. Legazpi', email: 'secbicol@sec.gov.ph' },
            { office: '5. Cebu', email: 'seccebu@sec.gov.ph' },
            { office: '6. Iloilo', email: 'sec_iloilo@sec.gov.ph' },
            { office: '7. Bacolod', email: 'secbacolod2018@gmail.com' },
            { office: '8. Tacloban', email: 'taceo@sec.gov.ph' },
            { office: '9. Davao', email: 'records_deo@sec.gov.ph' },
            { office: '10. Cagayan de Oro', email: 'reports_cdoeo@sec.gov.ph | seccdoe@sec.gov.ph' },
            { office: '11. Zamboanga', email: 'reports_zeo@sec.gov.ph' },
            { office: '12. Butuan', email: 'secbutuan@sec.gov.ph' },
          ].map((item) => (
            <React.Fragment key={item.office}>
              <span style={{ color: '#374151', whiteSpace: 'nowrap' }}>
                {item.office}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span style={{ color: '#9ca3af', fontSize: 10 }}>•</span>
                <strong style={{ color: '#111827', wordBreak: 'break-all' }}>
                  {item.email}
                </strong>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: 'Reportorial Requirements Templates',
    content: (
      <div>
        <p style={{ marginBottom: 8 }}>
          For copies and verifications of current reportorial form templates, please proceed to the links below:
        </p>
        <ol style={{ paddingLeft: 18 }}>
          <li>
            <b>
              <em>
                <a href="https://www.sec.gov.ph/reportorial-requirements/corporations-with-primary-licenses/" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  Corporations with Primary Licenses
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href="https://www.sec.gov.ph/reportorial-requirements/corporations-with-secondary-licenses/" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  Corporations with Secondary Licenses
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href="https://www.sec.gov.ph/reportorial-requirements/other-forms-of-financial-statements/" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  General Form and Special Forms for Financial Statements
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href="https://www.sec.gov.ph/wp-content/uploads/2019/11/Sworn-Statements-Format-v042513-Foundation.xlsx" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  Sworn Statement for Foundations <sup style={{ color: '#ef4444' }}>1</sup>
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href={file_nspo} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  Non-Stock and Non-Profit Organizations (NSPO) Form <sup style={{ color: '#ef4444' }}>2</sup>
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href={file_mc19s2019} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  Affidavit of Compliance for Existing and Prospective Online Lending Platforms
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href={file_gis_stock_2026} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  SEC Form GIS – Stock (2026 Version) - General Information Sheet (Annual)
                </a>
              </em>
            </b>
          </li>
          <li>
            <b>
              <em>
                <a href={file_gis_non_stock_2026} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN }}>
                  SEC Form GIS – Non Stock (2026 Version) - General Information Sheet
                </a>
              </em>
            </b>
          </li>
        </ol>
        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
          <p style={{ margin: '4px 0' }}>
            <sup style={{ color: '#ef4444' }}>1</sup> 2013 to 2018: Refer to the SEC Notice dated 18 April 2013.
          </p>
          <p style={{ margin: '4px 0' }}>
            <sup style={{ color: '#ef4444' }}>2</sup> Effective for AFS covering periods ending in December 31 2019 and onwards, and for interim financial statements starting the first quarter of 2020, and thereafter.
          </p>
        </div>
      </div>
    ),
  },
  {
    title: 'Checklist for Components of GIS & AFS',
    content: (
      <p style={{ margin: 0 }}>
        For a copy of the checklist, please proceed to this link:{' '}
        <a href={img_checklist} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_GREEN, fontWeight: 600 }}>
          <em>Checklist</em>
        </a>
      </p>
    ),
  },
];

// ── Accordion list with Sign-In style cards ──────────────────────────────────
function AccordionList({ items }) {
  return (
    <Collapse
      bordered={false}
      style={{ background: 'transparent' }}
      expandIconPosition="end"
    >
      {items.map((item, idx) => (
        <Collapse.Panel
          key={idx}
          forceRender={false}
          header={
            <span style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#111827',
            }}>
              {item.title}
            </span>
          }
          style={{
            marginBottom: 10,
            background: '#fafafa',
            borderRadius: 12,
            border: '1px solid #f3f4f6',
            overflow: 'hidden',
          }}
        >
          <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7, paddingTop: 4 }}>
            {item.content}
          </div>
        </Collapse.Panel>
      ))}
    </Collapse>
  );
}

const filingHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_FILING,
  },
};

// ── Dashboard Component ───────────────────────────────────────────────────────
const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeRange, setActiveRange] = useState('Today');
  const [tinPanelOpen, setTinPanelOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [tinBday, setTinBday] = useState({});

  const [todayCounts, setTodayCounts] = useState([0, 0, 0, 0]);
  const [weekCounts, setWeekCounts] = useState([0, 0, 0, 0]);
  const [monthCounts, setMonthCounts] = useState([0, 0, 0, 0]);

  const { user } = useAuth();
  const userId = user?.user_id;
  const companyId = user?.company_id;

  const storedData = JSON.parse(localStorage.getItem('user_data') || '{}');
  const firstName = storedData?.first_name || storedData?.username || storedData?.company_name || 'User';
  const companyName = storedData?.company_name || '';
  const companyStatus = storedData?.company_status || '';

  useEffect(() => {
    if (!userId) return;
    
    const fetchStats = async () => {
      try {
        await new Promise((resolve) => { setTimeout(resolve, 150); });
        
        setLoading(true);
        const periods = ['today', 'weekly', 'monthly'];
        const statuses = [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }];
        const requests = [];
        const keys = [];
        
        statuses.forEach((s, idx) => {
          periods.forEach((period) => {
            keys.push({ idx, period });
            requests.push(
              axios.get('https://api.fortisph.space/gateway/filing/forms/count/',
              {
                ...filingHeaders,
                params: {
                  user_id: userId,
                  status: s.id,
                  frequency: period,
                },
              })
            );
          });
        });
        
        const responses = await Promise.all(requests);
        const today = new Array(4).fill(0);
        const week = new Array(4).fill(0);
        const month = new Array(4).fill(0);
        
        keys.forEach((k, i) => {
          const count = responses[i]?.data?.count || 0;
          if (k.period === 'today') today[k.idx] = count;
          if (k.period === 'weekly') week[k.idx] = count;
          if (k.period === 'monthly') month[k.idx] = count;
        });
        setTodayCounts(today);
        setWeekCounts(week);
        setMonthCounts(month);
      } catch (e) {
        console.error('Failed to fetch stats:', e);
        message.error('Failed to load dashboard stats.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [userId]);

  useEffect(() => {
    const verified = tinBday?.is_verified;
    const compStatus = companyDetails?.status__name;
    const isMatch = tinBday?.is_tin_match;

    const shouldOpen =
      compStatus === 'Pending' ||
      verified !== true ||
      (isMatch !== true && isMatch !== null);

    setTinPanelOpen(!!shouldOpen);
  }, [tinBday, companyDetails]);

  const datasetsByRange = {
    Today: todayCounts,
    Week: weekCounts,
    Month: monthCounts,
  };
  const ranges = ['Today', 'Week', 'Month'];
  const values = datasetsByRange[activeRange] || [0, 0, 0, 0];
  const total = values.reduce((a, b) => a + b, 0);
  const acceptedFromRange = values[2];
  const raw = total ? Math.round((acceptedFromRange / total) * 100) : 0;
  const percentAccepted = Math.min(raw, 100);

  const chartColors = ['#0ea5e9', '#a855f7', '#22c55e', '#f97316'];
  const chartLabels = ['Uploaded', 'Submitted', 'Accepted', 'Reverted'];

  const miniStats = [
    { label: 'Uploaded', value: values[0], color: '#0ea5e9' },
    { label: 'Submitted', value: values[1], color: '#a855f7' },
    { label: 'Accepted', value: values[2], color: '#22c55e' },
    { label: 'Reverted', value: values[3], color: '#f97316' },
  ];

  return (
    <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>

      {/* ── Welcome Header ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: 0, letterSpacing: '-0.02em' }}>
              Welcome back, {firstName}
            </h1>
            {companyName && (
              <p style={{ margin: '4px 0 0', fontSize: 14, color: '#9ca3af' }}>{companyName}</p>
            )}
          </div>
          {companyStatus && (
            <Tag
              color={companyStatus === 'Verified' ? 'success' : companyStatus === 'Pending' ? 'warning' : 'default'}
              style={{ fontSize: 12, padding: '4px 12px', borderRadius: 999, margin: 0, fontWeight: 600 }}
            >
              {companyStatus}
            </Tag>
          )}
        </div>
      </div>

      {/* ── Main Content Row ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap', flex: 1, width: '100%' }}>

        {/* Left Column: Chart Card & Support */}
        <div style={{ flex: '1 1 360px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ ...CARD, padding: '28px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Card Header & Filter Pills */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Forms Overview</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#9ca3af' }}>Submission status breakdown</p>
              </div>
              <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 999, padding: 3, gap: 2 }}>
                {ranges.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setActiveRange(r)}
                    style={{
                      border: 'none', cursor: 'pointer', borderRadius: 999,
                      padding: '4px 12px', fontSize: 12, fontWeight: 600,
                      background: activeRange === r ? BRAND_GREEN : 'transparent',
                      color: activeRange === r ? '#fff' : '#6b7280',
                      transition: 'all 0.15s ease-in-out',
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Doughnut Chart */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <Suspense fallback={<div style={{ width: 200, height: 200, borderRadius: '50%', background: '#f3f4f6' }} />}>
                <DynamicDoughnutChart 
                  total={total} 
                  values={values} 
                  chartLabels={chartLabels} 
                  chartColors={chartColors} 
                  percentAccepted={percentAccepted} 
                />
              </Suspense>
            </div>

            {/* 2x2 Mini Stat Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 'auto' }}>
              {miniStats.map(({ label, value, color }) => (
                <div key={label} style={{
                  borderRadius: 14, padding: '14px 16px',
                  background: `${color}0D`,
                  border: `1px solid ${color}25`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6b7280' }}>
                      {label}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: loading ? '#d1d5db' : '#111827', lineHeight: 1, letterSpacing: '-0.02em' }}>
                    {loading ? '–' : value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Support Link Box */}
          <div style={{ ...CARD, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke={BRAND_GREEN} strokeWidth="1.8" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke={BRAND_GREEN} strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="17" r=".5" fill={BRAND_GREEN} stroke={BRAND_GREEN} />
            </svg>
            <span style={{ fontSize: 14, color: '#4b5563', fontWeight: 500 }}>Need help?</span>
            <Button
              type="link"
              href="https://imessage.sec.gov.ph"
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: 0, fontSize: 14, color: BRAND_GREEN, fontWeight: 600, marginLeft: 'auto' }}
            >
              Contact Support →
            </Button>
          </div>
        </div>

        {/* Right Column: Tabbed Announcements, Notices, & Templates */}
        <div style={{ flex: '1 1 360px', minWidth: 0 }}>
          <div style={{ 
            ...CARD, 
            overflow: 'hidden', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            contain: 'layout style paint',
            contentVisibility: 'auto',
          }}>
            <Tabs
              defaultActiveKey="announcements"
              style={{ padding: '8px 24px 0' }}
              destroyInactiveTabPane
              items={[
                {
                  key: 'announcements',
                  label: <span style={{ fontWeight: 600, fontSize: 15 }}>Announcements</span>,
                  children: (
                    <div style={{ padding: '8px 0 16px' }}>
                      <AccordionList items={announcements} />
                    </div>
                  ),
                },
                {
                  key: 'notices',
                  label: <span style={{ fontWeight: 600, fontSize: 15 }}>Notices</span>,
                  children: (
                    <div style={{ padding: '8px 0 16px' }}>
                      <AccordionList items={notices} />
                    </div>
                  ),
                },
                {
                  key: 'templates',
                  label: <span style={{ fontWeight: 600, fontSize: 15 }}>Templates</span>,
                  children: (
                    <div style={{ padding: '8px 0 16px' }}>
                      <AccordionList items={templates} />
                    </div>
                  ),
                },
              ]}
            />
          </div>

          <Drawer
            title={<span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Update Your Information</span>}
            placement="right"
            width={420}
            open={tinPanelOpen}
            onClose={() => setTinPanelOpen(false)}
            destroyOnClose
            closable
            headerStyle={{
              background: BRAND_GREEN,
              borderBottom: 'none',
            }}
            bodyStyle={{
              padding: 0,
              background: '#f8fafc',
            }}
          >
            <TinBday
              tin_bday={tinBday}
              user_details={companyDetails}
              companyId={companyId}
              toggleUpdateInfo={() => {
                setTinPanelOpen(false);
                message.success('Information updated successfully');
              }}
              alert={(type, msg) => {
                if (type === 'Success') message.success(msg);
                else message.error(msg);
              }}
            />
          </Drawer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;